/**
 * ClipStudio — "Prompt → Video".
 *
 * Sora 2 has been wired in the backend since the image pipeline landed, but
 * it was only reachable by asking in chat. This is its own front door: one
 * line of description, pick a length and a shape, and the footage lands in
 * Creations. Not a project — a single shot you can drop into a scene.
 *
 * Deliberately honest about the wait (a render is minutes, not seconds) and
 * about the cost, because it's the most expensive thing in Studio: 30 credits
 * a second, 100 for pro.
 */
import { useEffect, useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const LENGTHS = [4, 8, 12];
const SHAPES = [
  { id: "1024x1792", label: "Vertical", hint: "Reels, Shorts, TikTok" },
  { id: "1280x720",  label: "Wide",     hint: "YouTube" },
  { id: "1024x1024", label: "Square",   hint: "Feed" },
];

const absolute = (path) =>
  path?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

export function ClipStudio({ onBack }) {
  const [prompt, setPrompt] = useState("");
  const [seconds, setSeconds] = useState(4);
  const [shape, setShape] = useState("1024x1792");
  const [pro, setPro] = useState(false);
  const [job, setJob] = useState(null);      // { status, url }
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const cost = seconds * (pro ? 100 : 30);
  const busy = job && (job.status === "queued" || job.status === "running");

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(tickRef.current);
  }, []);

  const start = async () => {
    const p = prompt.trim();
    if (p.length < 3) { toast.error("Describe the shot first"); return; }
    try {
      const { data } = await api.post("/create/video", {
        prompt: p, size: shape, duration: seconds,
        model: pro ? "sora-2-pro" : "sora-2",
      });
      setJob({ status: data.status, job_id: data.job_id });
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/create/video/${data.job_id}`);
          if (r.data.status === "done" && r.data.url) {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
            setJob(r.data);
            toast.success("Your shot is ready — it's in Creations too");
          } else if (r.data.status === "error") {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
            setJob(null);
            toast.error(r.data.error || "That shot didn't come out — try rewording it");
          } else {
            setJob(cur => ({ ...(cur || {}), status: r.data.status }));
          }
        } catch (_) { /* keep polling — a blip shouldn't kill the job */ }
      }, 5000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "insufficient_credits") {
        toast.error(`That shot needs ${d.needed} credits and you have ${d.available}.`);
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Couldn't start that render");
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 md:p-10" data-testid="studio-clip">
      <button
        onClick={onBack}
        className="text-[12px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] inline-flex items-center gap-1.5 mb-6"
        data-testid="studio-clip-back"
      >
        <L.ArrowLeft size={13}/> All of Studio
      </button>

      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-cyan-dark)]">
        Prompt → Video
      </div>
      <h1 className="font-display text-3xl sm:text-4xl mt-3">Describe a shot.</h1>
      <p className="mt-3 text-[var(--rammy-text-sec)] leading-relaxed">
        Say it the way you'd brief a camera operator — what's in frame, the light, the
        movement. Rammy hands back real footage.
      </p>

      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={4}
        maxLength={900}
        placeholder="A slow push through morning fog over a still lake, low sun, birds lifting off the water"
        className="mt-6 rounded-2xl resize-y border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
        data-testid="studio-clip-prompt"
      />

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-2">Length</div>
          <div className="flex gap-1.5" data-testid="studio-clip-lengths">
            {LENGTHS.map(s => (
              <button
                key={s}
                onClick={() => setSeconds(s)}
                aria-pressed={seconds === s}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  seconds === s
                    ? "bg-[var(--rm-ink-900)] text-white border-[var(--rm-ink-900)]"
                    : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                }`}
                data-testid={`studio-clip-length-${s}`}
              >
                {s}s
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)] mb-2">Shape</div>
          <div className="flex flex-wrap gap-1.5" data-testid="studio-clip-shapes">
            {SHAPES.map(s => (
              <button
                key={s.id}
                onClick={() => setShape(s.id)}
                title={s.hint}
                aria-pressed={shape === s.id}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  shape === s.id
                    ? "bg-[var(--rm-ink-900)] text-white border-[var(--rm-ink-900)]"
                    : "bg-[var(--rm-surface)] border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]"
                }`}
                data-testid={`studio-clip-shape-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="mt-5 flex items-start gap-2.5 cursor-pointer" data-testid="studio-clip-pro-row">
        <input
          type="checkbox"
          checked={pro}
          onChange={(e) => setPro(e.target.checked)}
          className="mt-0.5 accent-[var(--rammy-cyan-dark)]"
          data-testid="studio-clip-pro"
        />
        <span className="text-[12.5px] text-[var(--rammy-text-sec)] leading-relaxed">
          <span className="font-medium text-[var(--rammy-text)]">Best quality</span> — sharper
          and steadier, and it costs more than three times as much. Worth it for a hero shot,
          not for a test.
        </span>
      </label>

      <div className="mt-7 rounded-2xl bg-[var(--rm-ink-900)] text-white p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Render</div>
          <div className="text-sm mt-1">
            {busy
              ? `Working on it — ${elapsed}s in. Renders take a few minutes.`
              : `${seconds} seconds, ${pro ? "best quality" : "standard"}.`}
          </div>
          <div className="font-mono text-[11px] text-white/45 mt-1" data-testid="studio-clip-cost">
            uses {cost.toLocaleString()} credits
          </div>
        </div>
        <Button
          onClick={start}
          disabled={busy}
          className="btn-pill btn-primary h-11 px-5"
          data-testid="studio-clip-go"
        >
          {busy
            ? <><L.Loader2 size={15} className="animate-spin mr-1.5"/> Rendering…</>
            : <><L.Sparkles size={15} className="mr-1.5"/> Make the shot</>}
        </Button>
      </div>

      {job?.status === "done" && job.url && (
        <div className="mt-6" data-testid="studio-clip-result">
          <video
            src={absolute(job.url)}
            controls
            className="w-full max-h-[460px] rounded-2xl bg-black"
            data-testid="studio-clip-video"
          />
          <p className="mt-2 text-[12px] text-[var(--rammy-text-muted)]">
            Saved to Creations — you can drop it into a scene from there.
          </p>
        </div>
      )}
    </div>
  );
}

export default ClipStudio;
